import { inject, Injectable } from '@angular/core';
import { Country, Reservation, StatusForAgentEnum, TouristDocumentDto } from '@appTypes/api.types';
import { ApiErrorResponse, errorResponseWithTrace } from '@core/interceptors/api.interceptor';
import { deepClean, fileDownload } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { TranslateService } from '@ngx-translate/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { ResTouristDocsUploadFormComponent } from '@pages/reservations-page/components/reservations-result/res-tourist-docs-upload-form/res-tourist-docs-upload-form.component';
import { ResTouristEditFormComponent } from '@pages/reservations-page/components/reservations-result/res-tourist-edit-form/res-tourist-edit-form.component';
import { ReservationDocsComponent } from '@pages/reservations-page/components/reservations-result/reservation-docs/reservation-docs.component';
import { ReservationPriceDetailsComponent } from '@pages/reservations-page/components/reservations-result/reservation-price-details/reservation-price-details.component';
import {
  RES_TOURIST_DOCUMENT_UPLOAD_FAILED_MESSAGE,
  RES_TOURIST_DOCUMENT_UPLOAD_STATUS_CHANGED_MESSAGE,
  RESERVATION_DOCUMENT_DOWNLOAD_FAILED_MESSAGE,
  RESERVATION_DOCUMENT_UNAVAILABLE_ERROR_CODE,
  RESERVATION_DOCUMENT_UNAVAILABLE_MESSAGE
} from '@pages/reservations-page/reservations.const';
import { SearchReservationResult } from '@pages/reservations-page/reservations.type';
import {
  reservationTouristFullName,
  resolveReservationStatusDetails,
  resolveTouristDocumentUploadRefusalMessage
} from '@pages/reservations-page/reservations.utils';
import { ReservationsApiService } from '@pages/reservations-page/services/reservations-api.service';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { DynamicModalService } from '@shared/modules/dynamic-modal/dynamic-modal.service';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { catchError, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import {
  reservationDeleteTouristDocument,
  reservationDownloadTouristDocument,
  reservationGetTouristDocuments,
  reservationOpenDocuments,
  reservationOpenPriceDetails,
  reservationOpenTouristDocUploadForm,
  reservationOpenTouristEditForm,
  reservationRefresh,
  reservationResetSearchResult,
  reservationsDownloadDocument,
  reservationsGetCountries,
  reservationsReset,
  reservationsSearch,
  reservationUploadTouristDocument
} from './reservations.action';
import {
  ReservationsSearchFormDataModel,
  ReservationsStateModel,
  ReservationsTouristDocumentsUploadFormModel
} from './reservations.model';

const defaults = new ReservationsStateModel();

/**
 * Picks the text shown when a document download fails. The API's own message wins. When the
 * document was closed in NugiosCore (KM-5777) and the API sent nothing usable — no text, or only
 * the error code echoed back because its localization entry is missing — the agent still gets the
 * agreed message rather than a bare code or the generic failure.
 */
const resolveDocumentDownloadErrorMessage = ({ errorCode, displayErrorMessage }: ApiErrorResponse): string => {
  if (displayErrorMessage && displayErrorMessage !== errorCode) return displayErrorMessage;
  return errorCode === RESERVATION_DOCUMENT_UNAVAILABLE_ERROR_CODE
    ? RESERVATION_DOCUMENT_UNAVAILABLE_MESSAGE
    : RESERVATION_DOCUMENT_DOWNLOAD_FAILED_MESSAGE;
};

/** One entry per country across the responses of several sales modes, first occurrence first (website-web #2). */
export function mergeDestinationCountries(lists: Country[][]): Country[] {
  const seen = new Set<string>();
  const merged: Country[] = [];
  for (const list of lists)
    for (const country of list) {
      const key = (country.countryCode ?? country.isO2 ?? country.countryName ?? '').toUpperCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(country);
    }
  return merged;
}

@State<ReservationsStateModel>({
  name: 'reservations',
  defaults
})
@Injectable()
export class ReservationsState {
  readonly #catalogueApi = inject(CatalogueApiService);
  readonly #api = inject(ReservationsApiService);

  readonly #dynamicModalService = inject(DynamicModalService);
  readonly #confirmService = inject(ConfirmService);
  readonly #translateService = inject(TranslateService);

  @Action(reservationsReset)
  reservationsReset({ setState }: StateContext<ReservationsStateModel>) {
    setState(defaults);
  }
  @Action(reservationsGetCountries, { cancelUncompleted: true })
  reservationsGetCountries(
    { getState, setState }: StateContext<ReservationsStateModel>,
    { refresh }: reservationsGetCountries
  ) {
    const {
      searchFormData: { countries }
    } = getState();

    if (countries.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Country[]>) => {
      setState(
        patch<ReservationsStateModel>({
          searchFormData: patch<ReservationsSearchFormDataModel>({
            countries: new RequestStateModel(stateParams)
          })
        })
      );
    };

    updateState(new DataLoadStatusModel('loading'));

    // website-web #2: the list offered only the TOUR destinations (onlyHotels: false), so a hotel booking in Thailand could not be
    // searched by destination. A reservation can hold any of the four ways of selling: the union of their destinations, one entry per
    // country, in the order the modes list them.
    const salesModes = ['package', 'hotelonly', 'avia', 'excursions'];
    return forkJoin(
      salesModes.map((salesMode) =>
        this.#catalogueApi.getDestinationCountries({ salesMode }).pipe(catchError(() => of(null)))
      )
    ).pipe(
      map((responses) => mergeDestinationCountries(responses.map((r) => r?.countries ?? []))),
      tap((countries) => updateState(countries)),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(reservationsSearch)
  reservationsSearch(
    { getState, patchState, setState }: StateContext<ReservationsStateModel>,
    { paging }: reservationsSearch
  ) {
    const {
      searchResults: { loadState },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;

    const payload = deepClean(searchForm.model);
    if (paging !== undefined) {
      const pagingRecords = getState().searchResults.response?.pagingRecords;
      if (pagingRecords !== undefined) paging = { ...pagingRecords, ...paging };
    }
    if (!paging?.totalCount) {
      patchState({
        searchResults: new RequestStateModel<SearchReservationResult>(new DataLoadStatusModel('loading'))
      });
    } else {
      setState(
        patch<ReservationsStateModel>({
          searchResults: patch<RequestStateModel<SearchReservationResult>>({
            loadState: new DataLoadStatusModel('loading')
          })
        })
      );
    }
    const pageRowCount = paging?.pageRowCount ?? 25;

    return this.#api.getReservations({ ...payload, ...paging, pageRowCount }).pipe(
      tap((response) => {
        patchState({
          searchResults: new RequestStateModel<SearchReservationResult>({
            ...response,
            pagingRecords: {
              ...paging,
              pageNumber: response.pageNumber,
              pageCount: response.pageCount,
              totalCount: response.totalCount
            }
          })
        });
      }),
      catchError((e) => {
        patchState({
          searchResults: new RequestStateModel<SearchReservationResult>(
            new DataLoadStatusModel('error', e?.displayErrorMessage, errorResponseWithTrace(e))
          )
        });
        return throwError(() => e);
      })
    );
  }

  /**
   * Re-reads one reservation and swaps it into the search results, keeping the rest of the page
   * as it is. A reservation the API no longer answers for (or one that is not in the results) is
   * left untouched.
   */
  @Action(reservationRefresh)
  reservationRefresh(
    { getState, setState }: StateContext<ReservationsStateModel>,
    { reservationId }: reservationRefresh
  ) {
    return this.#api.getReservations({ reservationId }).pipe(
      tap(({ reservations }) => {
        const reservation = reservations?.find(({ id }) => id === reservationId);
        const { response } = getState().searchResults;
        if (!reservation || !response?.reservations?.some(({ id }) => id === reservationId)) return;
        setState(
          patch<ReservationsStateModel>({
            searchResults: patch<RequestStateModel<SearchReservationResult>>({
              response: {
                ...response,
                reservations: response.reservations.map((item) => (item.id === reservationId ? reservation : item))
              }
            })
          })
        );
      })
    );
  }

  @Action(reservationsDownloadDocument)
  reservationsDownloadDocument(
    { patchState }: StateContext<ReservationsStateModel>,
    { url, name, preferProvidedName }: reservationsDownloadDocument
  ) {
    patchState({
      downloadDocumentLoadState: new DataLoadStatusModel('loading', { url })
    });
    return this.#api.getDocument(url).pipe(
      tap((response) => {
        patchState({
          downloadDocumentLoadState: new DataLoadStatusModel('completed')
        });

        const blob = response.body as Blob;

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = name; // Default filename

        if (contentDisposition && !preferProvidedName) {
          const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        fileDownload(blob, filename);
      }),
      catchError((error: ApiErrorResponse) => {
        const message = resolveDocumentDownloadErrorMessage(error);
        patchState({
          downloadDocumentLoadState: new DataLoadStatusModel<{ url: string }>('error', message)
        });

        this.#confirmService.confirm(message, {
          type: 'error',
          activityId: error.activityId
        });
        return throwError(() => error);
      })
    );
  }

  @Action(reservationOpenPriceDetails)
  reservationOpenPriceDetails(ctx: StateContext<ReservationsStateModel>, { reservation }: reservationOpenPriceDetails) {
    return this.#dynamicModalService.open({
      component: ReservationPriceDetailsComponent,
      styleClass: 'w-[500px]! h-[400px]!',
      data: reservation
    });
  }

  @Action(reservationOpenTouristEditForm)
  reservationOpenTouristEditForm(
    ctx: StateContext<ReservationsStateModel>,
    { tourist }: reservationOpenTouristEditForm
  ) {
    return this.#dynamicModalService.open({
      component: ResTouristEditFormComponent,
      title: 'Edit Tourist Data - ' + reservationTouristFullName(tourist),
      styleClass: 'w-[650px]! h-[450px]!',
      data: tourist
    });
  }

  /**
   * Opens the per-tourist **Upload Documents** popup on a fresh documents slice, so nothing of the
   * tourist opened before — their list, a refused upload — shows while this one loads.
   */
  @Action(reservationOpenTouristDocUploadForm)
  reservationOpenTouristDocUploadForm(
    { patchState }: StateContext<ReservationsStateModel>,
    { payload }: reservationOpenTouristDocUploadForm
  ) {
    patchState({ touristDocumentsUploadForm: new ReservationsTouristDocumentsUploadFormModel() });
    return this.#dynamicModalService.open({
      component: ResTouristDocsUploadFormComponent,
      title: 'Upload Documents - ' + reservationTouristFullName(payload.tourist),
      styleClass: 'w-[550px]! h-[550px]!',
      data: payload
    });
  }
  @Action(reservationOpenDocuments)
  reservationOpenDocuments(ctx: StateContext<ReservationsStateModel>, { reservation }: reservationOpenDocuments) {
    return this.#dynamicModalService.open({
      title: 'Documents',
      closeButtonTestId: 'cabinet-documents-close-button',
      component: ReservationDocsComponent,
      styleClass: 'st:w-[600px]! h-[600px]!',
      data: reservation
    });
  }

  @Action(reservationResetSearchResult)
  reservationResetSearchResult({ patchState }: StateContext<ReservationsStateModel>) {
    patchState({
      searchResults: new RequestStateModel<SearchReservationResult>()
    });
  }
  /**
   * Uploads the document held in the form. The API re-checks the reservation status in NugiosCore
   * and refuses once it has left `New` (KM-5604). The dashboard only knows the status the search
   * carried, so on a refusal the reservation is re-read first — the row and the popup then show
   * the status it actually moved to — and only after that the agent gets the error dialog, naming
   * that status. The refusal also lands in `uploadState.errorMessage`, which keeps the popup's
   * Upload blocked should the re-read still say `New`. Any other failure goes through the error
   * dialog straight away.
   */
  @Action(reservationUploadTouristDocument)
  reservationUploadTouristDocument({ setState, getState, dispatch }: StateContext<ReservationsStateModel>) {
    const {
      touristDocumentsUploadForm: { form, uploadState }
    } = getState();
    if (uploadState.status === 'loading') return;

    if (!form.model) return;
    const { rfid } = form.model;
    const setUploadState = (state: DataLoadStatusModel) =>
      setState(
        patch<ReservationsStateModel>({
          touristDocumentsUploadForm: patch<ReservationsTouristDocumentsUploadFormModel>({ uploadState: state })
        })
      );

    setUploadState(new DataLoadStatusModel('loading'));

    return this.#api.uploadTouristDocument(form.model).pipe(
      tap(() => {
        setUploadState(new DataLoadStatusModel('completed'));
        dispatch(new reservationGetTouristDocuments(form.model!.touristId!, form.model!.rfid!));
        this.#confirmService.confirm('Document uploaded successfully', {
          type: 'success'
        });
      }),
      catchError((error: ApiErrorResponse) => {
        const refusalMessage = resolveTouristDocumentUploadRefusalMessage(error);
        if (refusalMessage === null) {
          setUploadState(new DataLoadStatusModel('error'));
          this.#confirmService.confirm(error.displayErrorMessage ?? RES_TOURIST_DOCUMENT_UPLOAD_FAILED_MESSAGE, {
            type: 'error',
            activityId: error.activityId
          });
          return throwError(() => error);
        }

        // a failed re-read must not hide the refusal — the agent then gets the API's own text
        const refresh$ = rfid ? dispatch(new reservationRefresh(rfid)).pipe(catchError(() => of(null))) : of(null);
        return refresh$.pipe(
          tap(() => {
            const refreshed = rfid
              ? getState().searchResults.response?.reservations?.find(({ id }) => id === rfid)
              : null;
            const message = this.#resolveRefusedUploadMessage(refusalMessage, refreshed?.statusForAgent);
            setUploadState(new DataLoadStatusModel('error', message));
            this.#confirmService.confirm(message, { type: 'error', activityId: error.activityId });
          }),
          switchMap(() => throwError(() => error))
        );
      })
    );
  }
  @Action(reservationGetTouristDocuments)
  reservationGetTouristDocuments(
    { setState }: StateContext<ReservationsStateModel>,
    { touristId, rfid }: reservationGetTouristDocuments
  ) {
    setState(
      patch<ReservationsStateModel>({
        touristDocumentsUploadForm: patch<ReservationsTouristDocumentsUploadFormModel>({
          documentsLoadState: new DataLoadStatusModel('loading')
        })
      })
    );

    return this.#api.getAllTouristDocuments(touristId, rfid).pipe(
      tap((response) => {
        setState(
          patch<ReservationsStateModel>({
            touristDocumentsUploadForm: patch<ReservationsTouristDocumentsUploadFormModel>({
              documentsLoadState: new DataLoadStatusModel('completed'),
              documents: response.documents ?? []
            })
          })
        );
      }),
      catchError((error: ApiErrorResponse) => {
        setState(
          patch<ReservationsStateModel>({
            touristDocumentsUploadForm: patch<ReservationsTouristDocumentsUploadFormModel>({
              documentsLoadState: new DataLoadStatusModel('error', error?.displayErrorMessage)
            })
          })
        );
        this.#confirmService.confirm(
          error.displayErrorMessage ?? 'Something went wrong while getting documents, please try again later',
          {
            type: 'error',
            activityId: error.activityId
          }
        );
        return throwError(() => error);
      })
    );
  }

  @Action(reservationDownloadTouristDocument)
  reservationDownloadTouristDocument(
    { setState }: StateContext<ReservationsStateModel>,
    { document, touristId, rfid }: reservationDownloadTouristDocument
  ) {
    setState(
      patch({
        touristDocumentsUploadForm: patch({
          downloadState: new DataLoadStatusModel('loading', { fileId: document.fileId })
        })
      })
    );
    return this.#api.downloadTouristDocument(document.fileId!, touristId, rfid).pipe(
      tap((response) => {
        setState(
          patch({
            touristDocumentsUploadForm: patch({
              downloadState: new DataLoadStatusModel('completed')
            })
          })
        );
        const blob = response.body as Blob;

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = document.name!; // Default filename

        if (contentDisposition) {
          const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        fileDownload(blob, filename);
      }),
      catchError((error: ApiErrorResponse) => {
        setState(
          patch({
            touristDocumentsUploadForm: patch({
              downloadState: new DataLoadStatusModel<Pick<TouristDocumentDto, 'fileId'>>(
                'error',
                error?.displayErrorMessage ?? "Can't download document"
              )
            })
          })
        );
        this.#confirmService.confirm(error.displayErrorMessage ?? "Can't download document", {
          type: 'error',
          activityId: error.activityId
        });
        return throwError(() => error);
      })
    );
  }

  @Action(reservationDeleteTouristDocument)
  reservationDeleteTouristDocument(
    { setState, getState }: StateContext<ReservationsStateModel>,
    { document, touristId, rfid }: reservationDeleteTouristDocument
  ) {
    setState(
      patch({
        touristDocumentsUploadForm: patch({
          deleteState: new DataLoadStatusModel('loading', { fileId: document.fileId })
        })
      })
    );
    return this.#api.deleteTouristDocument(document.fileId!, touristId, rfid).pipe(
      tap(() => {
        const {
          touristDocumentsUploadForm: { documents }
        } = getState();
        setState(
          patch({
            touristDocumentsUploadForm: patch({
              deleteState: new DataLoadStatusModel('completed'),
              documents: documents.filter((d) => d.fileId !== document.fileId)
            })
          })
        );
      }),
      catchError((error: ApiErrorResponse) => {
        setState(
          patch({
            touristDocumentsUploadForm: patch({
              deleteState: new DataLoadStatusModel<Pick<TouristDocumentDto, 'fileId'>>(
                'error',
                error?.displayErrorMessage ?? "Can't delete document"
              )
            })
          })
        );
        this.#confirmService.confirm(error.displayErrorMessage ?? "Can't delete document", {
          type: 'error',
          activityId: error.activityId
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * What the agent is told about a refused upload once the reservation was re-read: the status it
   * moved to, as the row shows it, when it is indeed no longer `New`; otherwise the API's own text.
   */
  #resolveRefusedUploadMessage(refusalMessage: string, statusForAgent?: StatusForAgentEnum): string {
    if (!statusForAgent || statusForAgent === StatusForAgentEnum.New) return refusalMessage;
    const { label } = resolveReservationStatusDetails(statusForAgent);
    return this.#translateService.instant(RES_TOURIST_DOCUMENT_UPLOAD_STATUS_CHANGED_MESSAGE, {
      status: this.#translateService.instant(label)
    });
  }

  /* Selectors */

  @Selector([ReservationsState])
  static countries({ searchFormData: { countries } }: ReservationsStateModel) {
    return countries;
  }
  @Selector([ReservationsState])
  static searchResultsLoadState({ searchResults: { loadState } }: ReservationsStateModel) {
    return loadState;
  }
  @Selector([ReservationsState])
  static searchResults({ searchResults }: ReservationsStateModel) {
    return searchResults;
  }
  /** ADLC-4: the Status the search form holds - Overdued switches the "payment information not available" box to its Overdued wording. */
  @Selector([ReservationsState])
  static searchStatusForAgent({ searchForm }: ReservationsStateModel) {
    return searchForm?.model?.statusForAgent ?? null;
  }
  @Selector([ReservationsState])
  static downloadDocumentLoadState({ downloadDocumentLoadState }: ReservationsStateModel) {
    return downloadDocumentLoadState;
  }
  @Selector([ReservationsState])
  static touristDocumentUploadState({ touristDocumentsUploadForm: { uploadState } }: ReservationsStateModel) {
    return uploadState;
  }
  @Selector([ReservationsState])
  static touristDocuments({ touristDocumentsUploadForm: { documents, documentsLoadState } }: ReservationsStateModel) {
    return { documents, loadState: documentsLoadState };
  }
  @Selector([ReservationsState])
  static touristdocumentsLoadState({ touristDocumentsUploadForm: { documentsLoadState } }: ReservationsStateModel) {
    return documentsLoadState;
  }
  @Selector([ReservationsState])
  static touristDocumentDownloadState({ touristDocumentsUploadForm: { downloadState } }: ReservationsStateModel) {
    return downloadState;
  }
  @Selector([ReservationsState])
  static touristDocumentDeleteState({ touristDocumentsUploadForm: { deleteState } }: ReservationsStateModel) {
    return deleteState;
  }

  /** One reservation of the search results, by id — `null` once it is not (or no longer) in them. */
  static reservation(reservationId: string) {
    return createSelector(
      [ReservationsState],
      ({ searchResults: { response } }: ReservationsStateModel): Reservation | null =>
        response?.reservations?.find(({ id }) => id === reservationId) ?? null
    );
  }

  static findCountryName(code: string) {
    return createSelector(
      [ReservationsState],
      ({ searchFormData: { countries } }: ReservationsStateModel): string | null => {
        if (!countries.response?.length) return null;
        return countries.response.find((c) => c.countryCode === code)?.countryName ?? null;
      }
    );
  }
}
